/**
 * Mijoz tomonidagi tekshiruv — `apps/server/src/api/routes/settings.ts`dagi Zod
 * sxemalarning AYNAN o'zi, faqat frontendda takrorlangan (webapp `zod`ga to'g'ridan-to'g'ri
 * bog'liq emas — `package.json`da yo'q, shuning uchun YANGI kutubxona qo'shmaslik uchun
 * `checkInput.ts`/`parseViewsInput()` naqshidagi kabi sof funksiyalar bilan yozilgan).
 *
 * MUHIM: bu — faqat tezkor fikr-mulohaza (admin xatoni darhol ko'radi, so'rov yubormay
 * turib). Server BARIBIR o'z sxemasi bilan qayta tekshiradi (yagona haqiqiy manba) — bu
 * yerdagi chegaralar o'zgarsa-yu serverdagi o'zgarmasa, faqat "noqulay UX" (xato kech
 * ko'rinadi), XAVFSIZLIK teshigi EMAS.
 */
import type { RateTier } from '@ayub/shared'
import { parseIntegerInput } from './numericInput'
import type { TierRow } from './PayoutSection'

// ─────────────────────────────── Maydon qoidalari ───────────────────────────────

export function validateRatePer1000(value: number): string | null {
  if (!Number.isInteger(value)) return "1000 prosmotr uchun stavka butun son bo'lishi kerak"
  if (value <= 0) return "1000 prosmotr uchun stavka musbat bo'lishi kerak"
  if (value > 10_000_000) return '1000 prosmotr uchun stavka juda katta'
  return null
}

export interface TiersEvaluation {
  /** Yaroqli bo'lsa tozalangan bosqichlar, aks holda `null`. */
  value: RateTier[] | null
  error: string | null
}

/**
 * Bosqichli stavka jadvalini tekshiradi (server `rateTiersSchema` bilan bir xil qoidalar).
 * Har bosqichda stavka musbat butun bo'lishi shart; OXIRGIsidan boshqa har bosqichda prosmotr
 * eni musbat butun bo'lishi shart (oxirgi bosqich = "qolgan barchasi", eni e'tiborsiz → 0).
 * Kamida 1, ko'pi 10 bosqich.
 */
export function evaluateRateTiers(rows: TierRow[]): TiersEvaluation {
  if (rows.length === 0) return { value: null, error: "Kamida bitta bosqich bo'lishi kerak" }
  if (rows.length > 10) return { value: null, error: 'Bosqichlar soni 10 dan oshmasligi kerak' }

  const result: RateTier[] = []
  for (let i = 0; i < rows.length; i++) {
    const isLast = i === rows.length - 1
    const label = `${i + 1}-bosqich`

    const rate = parseIntegerInput(rows[i]!.ratePer1000)
    if (rate === null || rate <= 0) {
      return { value: null, error: `${label}: stavka musbat butun son bo'lishi kerak` }
    }
    if (rate > 10_000_000) return { value: null, error: `${label}: stavka juda katta` }

    let views = 0
    if (!isLast) {
      const parsed = parseIntegerInput(rows[i]!.views)
      if (parsed === null || parsed <= 0) {
        return { value: null, error: `${label}: prosmotr eni musbat butun son bo'lishi kerak` }
      }
      views = parsed
    }
    result.push({ views, ratePer1000: rate })
  }
  return { value: result, error: null }
}

export function validateAutoPayoutThreshold(value: number): string | null {
  if (!Number.isInteger(value)) return "Avtomatik to'lov chegarasi butun son bo'lishi kerak"
  if (value < 0) return "Avtomatik to'lov chegarasi manfiy bo'lishi mumkin emas"
  if (value > 1_000_000_000) return "Avtomatik to'lov chegarasi juda katta"
  return null
}

// 1..28 — 29/30/31-sana ba'zi oylarda umuman yo'q (server `routes/settings.ts`dagi
// `scheduledPayoutDaySchema` izohi bilan BIR XIL sabab).
export function validateScheduledPayoutDay(value: number): string | null {
  if (!Number.isInteger(value)) return "Rejali to'lov kuni butun son bo'lishi kerak"
  if (value < 1) return "Rejali to'lov kuni 1 dan kichik bo'lishi mumkin emas"
  if (value > 28)
    return "Rejali to'lov kuni 28 dan katta bo'lishi mumkin emas (ba'zi oylarda bunday sana yo'q)"
  return null
}

function validateCheckDays(value: number, label: string): string | null {
  if (!Number.isInteger(value)) return `${label} butun son bo'lishi kerak`
  if (value <= 0) return `${label} musbat bo'lishi kerak`
  if (value > 365) return `${label} 365 kundan katta bo'lishi mumkin emas`
  return null
}

export const validateFirstCheckDays = (value: number): string | null =>
  validateCheckDays(value, 'Tekshiruv kuni')

export const validateLinkWaitDays = (value: number): string | null =>
  validateCheckDays(value, 'Havola kutish muddati')

function validateNonNegativeDays(value: number, label: string): string | null {
  if (!Number.isInteger(value)) return `${label} butun son bo'lishi kerak`
  if (value < 0) return `${label} manfiy bo'lishi mumkin emas`
  if (value > 365) return `${label} 365 kundan katta bo'lishi mumkin emas`
  return null
}

export const validateReapplyAfterDays = (value: number): string | null =>
  validateNonNegativeDays(value, 'Qayta ariza berish muddati')

export function validateMinFollowers(value: number): string | null {
  if (!Number.isInteger(value)) return "Minimal obunachilar soni butun son bo'lishi kerak"
  if (value < 0) return "Minimal obunachilar soni manfiy bo'lishi mumkin emas"
  if (value > 100_000_000) return 'Minimal obunachilar soni juda katta'
  return null
}

// ─────────────────────────────── Tozalash + tekshirish birgalikda ───────────────────────────────

export interface FieldEvaluation {
  /** Yaroqli bo'lsa tozalangan son, aks holda `null` — chaqiruvchi shu bilan "hali saqlab
   * bo'lmaydi" holatini biladi. */
  value: number | null
  /** Foydalanuvchiga ko'rsatiladigan xabar, yaroqli bo'lsa `null`. */
  error: string | null
}

/**
 * Xom matnni (`ViewsInput`dagi kabi `type="text" inputMode="numeric"` maydondan) tozalab,
 * biznes qoidasi bilan bir yo'la tekshiradi. Bo'sh maydon ham xato hisoblanadi — bu yerdagi
 * HECH BIR sozlama ixtiyoriy emas (`SettingsUpdateRequest` PATCH'da ixtiyoriy, lekin FORMA
 * o'zi hech qachon bo'sh maydon bilan saqlanmasligi kerak).
 */
export function evaluateField(
  raw: string,
  validate: (value: number) => string | null,
): FieldEvaluation {
  if (raw.trim().length === 0) {
    return { value: null, error: 'Qiymat kiritilishi shart' }
  }
  const parsed = parseIntegerInput(raw)
  if (parsed === null) {
    return { value: null, error: 'Faqat raqam kiriting (masalan: 5000)' }
  }
  const message = validate(parsed)
  return { value: message ? null : parsed, error: message }
}

// ─────────────────────────────── Admin qo'shish formasi ───────────────────────────────

export function validateNewAdminTelegramId(raw: string): FieldEvaluation {
  if (raw.trim().length === 0) {
    return { value: null, error: 'Telegram ID kiritilishi shart' }
  }
  const parsed = parseIntegerInput(raw)
  if (parsed === null || parsed <= 0) {
    return { value: null, error: "Telegram ID musbat butun son bo'lishi kerak" }
  }
  return { value: parsed, error: null }
}

export function validateNewAdminName(raw: string): string | null {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return 'Ism kiritilishi shart'
  if (trimmed.length > 200) return 'Ism juda uzun'
  return null
}
